import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import HomeView from '../views/HomeView.vue'
import appLayout from '../layouts/appLayout.vue'
import cardLayout from '../layouts/cardLayout.vue'
import BillingView from '../views/BillingView.vue'
import AboutView from '../views/AboutView.vue'
import InviteView from '../views/InviteView.vue'
import LoginView from '../views/LoginView.vue'
import EmailLoginView from '../views/EmailLoginView.vue'
import SettingsView from '../views/SettingsView.vue'
import ProfilesView from '../views/ProfilesView.vue'
import MeetingsView from '../views/MeetingsView.vue'
import EventView from '../views/EventView.vue'
import PersonView from '../views/PersonView.vue'
import ReferralsView from '../views/ReferralsView.vue'
import SupportView from '../views/SupportView.vue'
import OnboardingTrial from '../views/onboardingTrial.vue'
import OnboardingHow from '../views/onboardingHow.vue'
import OnboardingView from '../views/onboardingView.vue'
import OnboardingSettings from '../views/onboardingSettings.vue'
import AuthCallbackView from '../views/AuthCallbackView.vue'
import AffiliateDashboard from '../views/AffiliateDashboard.vue'
import AdminView from '../views/AdminView.vue'
import SharedPersonView from '@/views/SharedPersonView.vue'
import UpdatesView from '@/views/UpdatesView.vue'
import { trackPageView } from '@/utils/analytics'

declare module 'vue-router' {
  interface RouteMeta {
    allowedUsers?: number[]
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'homeRoot',
          component: HomeView
        }
      ]
    },
    {
      path: '/billing',
      name: 'Billing',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'billingRoot',
          component: BillingView
        }
      ]
    },
    // {
    //   path: '/about',
    //   name: 'about',
    //   component: AboutView
    // },
    {
      path: '/invite',
      name: 'invite',
      component: cardLayout,
      children: [
        {
          path: '',
          name: 'acceptInvite',
          component: InviteView
        }
      ]
    },
    {
      path: '/login',
      name: 'login',
      component: cardLayout,
      children: [
        {
          path: '',
          name: 'loginRoot',
          component: LoginView
        },
        { path: 'email', component: EmailLoginView }
      ],
      meta: {
        public: true
      }
    },
    {
      path: '/settings',
      name: 'settings',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'settingsRoot',
          component: SettingsView
        }
      ]
    },
    {
      path: '/profiles',
      name: 'profiles',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'profilesRoot',
          component: ProfilesView
        }
      ]
    },
    {
      path: '/events',
      name: 'events',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'eventsRoot',
          component: MeetingsView
        },
        {
          path: '/events/:eventID',
          name: 'event',
          component: EventView
        },
        {
          path: '/event/:eventID/person/:personID',
          component: PersonView
        }
      ]
    },
    {
      path: '/referrals',
      name: 'referrals',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'referralsRoot',
          component: ReferralsView
        }
      ]
    },
    {
      path: '/affiliate',
      name: 'Affiliate',
      component: appLayout,
      meta: {
        allowedUsers: [1, 2]
      },
      children: [
        {
          path: '',
          name: 'affiliateRoot',
          component: AffiliateDashboard
        }
      ]
    },
    {
      path: '/support',
      name: 'support',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'supportRoot',
          component: SupportView
        }
      ]
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: appLayout,
      children: [
        {
          path: '',
          name: 'onboardingTrial',
          component: OnboardingTrial
        },
        {
          path: '2',
          name: 'onboardingHow',
          component: OnboardingHow
        },
        {
          path: 'calendars',
          name: 'onboardingCalendar',
          component: OnboardingView
        },
        {
          path: 'settings',
          component: OnboardingSettings
        }
      ]
    },
    {
      path: '/authcallback',
      name: 'authcallback',
      component: AuthCallbackView,
      meta: {
        public: true
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: appLayout,
      meta: {
        allowedUsers: [1, 2]
      },
      children: [
        {
          path: '',
          name: 'adminRoot',
          component: AdminView
        }
      ]
    },
    {
      path: '/shared/:sharedProfileID',
      name: 'shared-person',
      component: SharedPersonView,
      meta: {
        public: true
      }
    },
    {
      path: '/updates',
      name: 'updates',
      component: UpdatesView,
      meta: {
        public: true
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  console.log(to.path, {
    authenticated: authStore.isAuthenticated,
    settings: !!authStore.settings
  })

  // Check for allowed users in protected routes
  if (to.meta.allowedUsers) {
    const userID = authStore.user?.userID
    if (!userID || !to.meta.allowedUsers.includes(userID)) {
      return next({ path: '/' })
    }
  }

  if (!authStore.isAuthenticated && to.path === '/') {
    await authStore.checkWithServer().then((profile) => {
      if (profile) router.push('/')
      else router.push('/login')
    })
  }
  if (authStore.isAuthenticated) {
    if (!authStore.settings && !to.path.startsWith('/onboarding')) router.push('/onboarding')
    else {
      if (to.path === '/login') router.push('/')
      else next()
    }
  }
  if (!to.meta.public && !authStore.isAuthenticated) next({ path: '/login' })
  else next()
})

router.afterEach((to) => {
  trackPageView(to.fullPath)
})

export default router
